import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { useUser } from '/imports/hooks';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { PageFooter } from '/imports/ui/PageFooter';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { toastOnError } from '/imports/ui/utils';
import { createSolisCredential, getSolisCredentialsForOrg, SolisCredential, pullSolisStations } from '/imports/api/solisMeters';

const SolisMeterPageStyled = styled(Page)`
  .result {
    margin: 2rem 0;
    .buttons {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }
  }
  table {
    td {
      padding: 1rem;
      &:first-child {
        padding-left: 0;
      }
    }
  }
`;

/** Super-user page for managing Solis Cloud API credentials and pulling stations. */
export const SolisMeterPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;
  const orgId = user.orgId;
  const [apiUrl, setApiUrl] = useState<string | undefined>('https://www.soliscloud.com:13333');
  const [keyId, setKeyId] = useState<string>();
  const [keySecret, setKeySecret] = useState<string>();
  const [credentials, setCredentials] = useState<SolisCredential[]>([]);
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setCredentials(await getSolisCredentialsForOrg.call(orgId));
    })();
  }, [orgId]);
  /**
   * Pulls Solis stations for a credential and toasts the counts found/created.
   * @param credentialId - id of the Solis credential to pull stations for
   */
  const pullStations = toastOnError(async (credentialId: string) => {
    const { numSystems, numCreated } = await pullSolisStations.call(credentialId);
    toast.success(`${numSystems} ${strings.SystemsFound()} | ${numCreated} ${strings.Created()}`);
  });
  /** Creates a Solis credential from the form fields and refreshes the list. */
  const save = toastOnError(async () => {
    if (!apiUrl || !keyId || !keySecret || !orgId) return;
    await createSolisCredential.call({ apiUrl, keyId, keySecret });
    setKeyId(undefined);
    setKeySecret(undefined);
    setCredentials(await getSolisCredentialsForOrg.call(orgId));
  });
  return (
    <SolisMeterPageStyled className="page">
      <PageHeading
        left={<h1>Solis</h1>}
      />
      <PageBody>
        <Form>
          <div className="field-group">
            <FormField field="apiUrl">
              <label>{strings.ApiUrl()}</label>
              <Input value={apiUrl} onChange={(apiUrl) => setApiUrl(apiUrl)} />
            </FormField>
            <FormField field="keyId">
              <label>{strings.KeyId()}</label>
              <Input value={keyId} onChange={(keyId) => setKeyId(keyId)} />
            </FormField>
            <FormField field="keySecret">
              <label>{strings.KeySecret()}</label>
              <Input value={keySecret} onChange={(keySecret) => setKeySecret(keySecret)} />
            </FormField>
          </div>
          <div><Button onClick={() => save()}>{strings.Save()}</Button></div>
        </Form>
        {
          credentials.length ?
            <div>
              <h2>{strings.Accounts()}</h2>
              <table>
                <tbody>
                  {
                    credentials.map((c) => (
                      <tr key={c._id}>
                        <td>{c.apiUrl}</td>
                        <td>{c.keyId}</td>
                        <td><Button onClick={() => pullStations(c._id)}>{strings.PullSystems()}</Button></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            :
            null
        }
      </PageBody>
      <PageFooter
      />
    </SolisMeterPageStyled>
  );
};
